/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* editmodelscherm.ts
* 16-12-2019 - Jelmer Jellema - Spin in het Web B.V.
*
* Abstracte parent van schermen met een dialoog voor één model instance
* We regelen de code zoveel mogeijk algemeen.
* Dit is een generieke class voor de juiste recordclass
* Krijgt een injector van de concrete classes
*
* Verwacht:
* <form #form="ngForm"> Als het hoofdform
*
*/

import {AfterViewInit, Directive, HostBinding, Input, OnInit, ViewChild} from '@angular/core';
import {Appscherm} from './appscherm';
import {Datarecord, Datarecords, Recordquery} from '../../services/api/api.service';
import {NgForm} from '@angular/forms';
import {cloneDeep, sortBy} from 'lodash-es';
import {hash} from '../../app/types';
import {Basemodel} from '../../models/basemodel';

@Directive() //nodig want classes met ng-events of angular decorators moeten gedecoreerd zin
export abstract class Editmodelscherm<Recordclass extends typeof Basemodel> extends Appscherm implements OnInit, AfterViewInit {
  //zet de .editscherm class voor algemene styling

  @HostBinding('class.editscherm') readonly _hostclasseditscherm = true;
  @Input('id') editId: any = null; //id of record to edit, we will create a model for it
  @Input('editRecord') editRecord: Recordclass | hash<any> = null; //the model to edit (new or available), as model or data. If model, it will be cloned

  //warning op volgende regel komt omdat het form in de concrete subclasses zit
  @ViewChild('form', {static: false}) form: NgForm;

  protected abstract model: Recordclass;
  protected throughOwner: typeof Basemodel = null; //als gegeven, dan halen we data op via de opgegeven owner
  protected defaultSubmodels: (typeof Basemodel)[] = []; //baseModel.fromId model-array die gebruikt wordt bij het ophalen vn het record met fromId. Standaard leeg = niets. (null = alles)
  protected defaultSuboptions: [typeof Basemodel, hash<any>][] = []; //suboptions bij het ophalen van de data met baseModel.fromId
  protected aclModel: typeof Basemodel = null;

  protected readonly lookupQueries: hash<Recordquery |
    {
      model: typeof Basemodel,
      submodels?: (typeof Basemodel)[], //let op: default [], expliciet op null zetten voor 'alles'
      suboptions?: [typeof Basemodel, hash<any>][],
      throughOwner?: typeof Basemodel, //zie baseModel::getAll
      aclModel?: typeof Basemodel, //lookup-aclpermissie
      sortBy?: string //property waarop de lijst na ophalen gesorteerd moet worden (vooral nodig bij throughOwner)
    }> = {};
  //informatie die ook opgehaald moet worden. Key is de key in lookups en lookupHashes, value is een algemene recordquery of een hash met model en eventueel submodels en suboptions voor de getAll call van het model. Wij zetten geen aclmodel


  private action: 'save' | 'delete' = null; //wat zijn we aan het doen

  record: InstanceType<Recordclass> = null; //cast naar het juiste model
  canDelete: boolean = false; //wordt apart gezet
  readonly: boolean = false; //hebben wij besloten dat het readonly is?
  protected defaultRecord: Datarecord = {}; //the default data for a new record
  lookups: hash<Datarecords | Basemodel[]> = {}; //lookupdata per lookupkey
  lookupHashes: hash<hash<Datarecord | Basemodel>> = {}; //lookupdata per lookupkey, op id
  shouldReload: boolean = false; //flag, gaat op true bij datawijziging, en op false bij reload

  /**
   * Default datepickerconfig
   */
  readonly datepickerconfig = {
    displayMonths: 2
  };

  protected constructor(loglevel: string = 'warn') {
    super(loglevel);
  }

  public get isNieuw(): boolean {
    return !(this.editId || (<any>this.editRecord)?.id); //voor haaldata hebben we het nog op dataniveau
  }

  /**
   * Helper voor oude code zonder modellen. Pak de tabel uit het model
   */
  public get table(): string {
    return this.model.table;
  }

  async ngOnInit() {
    //init het record even met een lege modelinstance
    //dat voorkomt poblemen met ngmodel tijdens de async acties hieronder
    this.record = this.model.createNew(null, this.defaultRecord || {});
    // test acl
    if (await this.canReadonly()) {
      this.readonly = true; //voor nu, wordt opnieuw gezet in haaldata
      if (this.isNieuw) {
        //laat maar
        this.log.warn(`edit-nieuw maar readonly`);
        setTimeout(() => this.closeWindow());
        return;
      }
    } else if (! await this.canWrite()) {
      //geen toestemming
      this.log.warn(`Edit: geen toestemming`);
      setTimeout(() => this.closeWindow());
      return;
    }

    // this.readonly = false; //test

    //haal de data

    await this.haalData();

    //Zet observer op de datawijzigingen
    if (this.editId) {
      this.subscription(this.api.onRecordwijziging(this.table, this.editId).subscribe(wijziging => {
        if (wijziging.delete) {
          if (this.action !== 'delete') {
            //record is verwijderd, en niet door ons
            this.api.toast(`Een andere gebruiker heeft ${this.recordBeschrijving} verwijderd...`, 'warning');
            this.closeWindow();
            return;
          }
        } else if (this.action !== 'save') {
          //een ander slaat het op
          //this.api.toast(`Een andere gebruiker heeft ${this.recordBeschrijving} gewijzigd. Sluit of herlaad de gegevens.`, 'warning');
          this.shouldReload = true;
        }
      }));
    }
    this.subscription(this.api.onDatawijziging(Object.keys(this.lookupQueries)).subscribe(() => {
      //haal de lookups opnieuw op. Alleen de gewijzigde zijn niet meer in de cache
      this.haalLookups();
    }));

   await this.dataGezet(); //hook voor subclasses
  }

  /**
   * Overschrijfbare test of er readonlytoegang is
   * @protected
   */
  protected async canReadonly(): Promise<boolean> {
    return this.api.can(this.table, 'readonly');
  }

  /**
   * Overschrijfbare test of er schrijftoegang is
   * @protected
   */
  protected async canWrite(): Promise<boolean> {
    return this.api.can(this.table,'full');
  }

  ngAfterViewInit(): void {

    //nu is er het form
    //anders alles readonly
    if (this.readonly && this.form) {
      setTimeout(() => {
        this.form.form.disable();
      });
    }
  }

  /**
   * Get the data from the backend. Force
   * @private
   */
  private async haalData(forceFromBackend: boolean = false) {
    if (!this.model) {
      this.log.error('Model niet gezet', this);
      return;
    }
    let getid: any = null;
    if (this.editRecord) {
      //record to edit has been input. This can be a new one or an existing one. We will not get any data from the back for it
      //when forced reload and we have an id, we use it to get the data
      if (forceFromBackend && this.record.id) {
        getid = this.record.id;
      } else {
        if ( this.editRecord instanceof this.model) {
          this.record = <InstanceType<Recordclass>> this.editRecord.fullClone(); //clone this
          this.editId = this.record.id; //code checks for stuff throug this.editId
        } else {
          this.record = this.model.createNew(null, this.editRecord ?? this.defaultRecord);
          this.editId = null;
        }
      }
    } else if (this.editId) {
      getid = this.editId;
    }
    //if we have getid set, it's because we have to get the data from the backend
    if (getid) {
      //id to get de data
      try {
        //we gebruiken getAll vanwege mogelijkheden met throughowner en aclModel
        //voeg de benodigde id toe aan de suboptions
        const subOptions = cloneDeep(this.subOptions);
        let modelOptions = subOptions.find(su => su[0] === this.model);
        if (! modelOptions)
        {
          modelOptions = [this.model, {}];
          subOptions.push(modelOptions);
        }
        //we hebben nu de opties voor ons model
        modelOptions[1]['where'] ||= {}; //de where moet er zijn
        modelOptions[1]['where']['id'] = getid; //en hierop zoeken we

        const getResult = await this.model.getAll(this.subModels, subOptions,null, this.aclModel, this.throughOwner, true, !forceFromBackend);
        this.log.debug(`HAALDATA`,getResult);
        if (getResult.length) {
          this.record = getResult[0];
          this.editId = this.record.id; //code checks for stuff through this id
        }
        else {
          // noinspection ExceptionCaughtLocallyJS
          throw("NOT FOUND");
        }
      } catch (e) {
        this.log.error(e);
        this.closeWindow();
        return;
      }
    }
    //can the user delete this record?
    if (this.isNieuw) {
      this.canDelete = false;
    } else {
      //kanVerwijderen altijd apart, zonder caching
      this.canDelete = await this.api.canDelete(this.table, this.editId);
    }

    await this.haalLookups(); //wat niet gewijzigd is zit nog in de cache
    this.shouldReload = false;
    //opnieuw zetten readonly
    this.readonly = await this.canReadonly();
    //klaar: hook
    await this.afterHaaldata();
  }

  /**
   * Haal de lookups (opnieuw) op
   * @protected
   *
   * TODO: hoe casten we dit naar het juiste model zodat het direct
   * in templates gebruikt kan worden? Met een @lookup decorator ipv een
   * hash met lookupQueries?
   * @lookups({model: ...} Projecten: Project[]; ofzo?
   */
  protected async haalLookups() {
    for (let key of Object.keys(this.lookupQueries)) {
      let lookupdata = this.lookupQueries[key];
      let lookupQuery: Recordquery;

      try {
        let data: any;
        if ('model' in lookupdata) {
          //modelbased
          //let op: wij doen submodels standaard  [] en niet null
          //omdat je meestal geen submodels wil, expliciet op null
          ///zetten als je alles wilt, dus

          data = await lookupdata.model.getAll(
            ('submodels' in lookupdata) ? lookupdata.submodels : [],
            lookupdata.suboptions,
            null,
            lookupdata.aclModel,
            lookupdata.throughOwner
          );
          if (lookupdata.sortBy) {
            //sorteren (vooral nodig bij throughOwner)
            data = sortBy(data, lookupdata.sortBy);
          }
        } else {
          //het is een recordquery, dus kopiëren
          lookupQuery = cloneDeep(lookupdata);
          if (!('lookup' in lookupdata)) {
            //onze table
            lookupQuery.lookup = this.table;
          } else if (!lookupdata.lookup) {
            //expliciet false
            delete lookupQuery.lookup; //niet mee
          }
          data = <Datarecords> await this.api.cachedRecords(lookupQuery);
        }
        this.lookups[key] = data;
        if (this.lookups[key].length && this.lookups[key][0].id) {
          this.lookupHashes[key] = {};
          for (let r of this.lookups[key]) {
            this.lookupHashes[key][r.id] = r;
          }
        }
      } catch (e) {
        this.log.debug(`Error in ophalen lookup ${key}`);
        this.log.warn(e);
      }
    }
    //klaar: hook
    this.afterHaalLookups();
  }


  /////////////////// STANDAARD BUTTON HANDLERS ///////////////////////////////////

  /**
   * Force a reload from the backend
   */
  public async reload() {
    //opnieuw laden
    if (this.form) //zou er moeten zijn
    {
      this.form.resetForm();
      this.log.debug('form is reset');
    }
    await this.haalData(true);
  }

  /**
   * Save het record
   */
  public async save(): Promise<void> {
    if (this.readonly || (!this.canWrite())) {
      return;
    }
    try {
      const savedata = this.savedata(); //hook voor omzetten data voor het saven
      if (! savedata)
      {
        return; //geannuleerd;
      }
      this.log.debug(`Save`, this.record, savedata);
      this.action = 'save'; //bezig
      let res = await this.api.save({
        table: this.table,
        data: savedata
      });
      if (this.isNieuw) {
        this.record.id = res.saved; //id ook goed zetten, voor teruggeven resultaat
      }
      //gelukt
      this.form.form.markAsPristine();
      await this.afterSave(); //async, so this.actin remains 'save' when saving extra stuff etc
    } catch (e) {
      this.log.error(e);
    }
    this.action = null;
  }

  /**
   * Verwijder het record
   */

  public async delete(): Promise<void> {
    if (!this.canWrite()) {
      return;
    }
    try {
      if (await this.api.confirm('Verwijderen', `Weet je zeker dat je ${this.recordBeschrijving} wilt verwijderen?`)) {
        this.action = 'delete'; //bezig
        if (!this.isNieuw) {
          //ook echt weghalen
          await this.api.delete(this.table, this.editId);
        }
        //gelukt
        this.afterDelete();
      }
    } catch (e) {
      this.log.error(e);
    }
    this.action = null;
  }

  /**
   * Hook close-actie: moet er eerst bevestiging komen?
   */
  async canClose(): Promise<boolean> {
    if (this.isDirty) {
      return await this.api.confirm('Niet opgeslagen', `Er zijn niet opgeslagen wijzigingen. Weet je zeker dat je dit scherm wilt sluiten zonder op te slaan?`);
    }
    return true; //anders alles ok
  }

  /**
   * Overschrijfbare test of het scherm gewijzigd is
   * @protected
   */
  protected get isDirty(): boolean {
    return !! this.form?.dirty;
  }

  ////////////////////// TAAL - overschrijfbaar //////////////////

  /**
   * De beschrijving van dit record. Bijvoorbeeld "de gebruiker 'Sjaak'", of "deze vrachtwagen".
   */
  protected get recordBeschrijving(): string {
    return 'dit record';
  }

  /////////////////////////// Helpers voor schermen //////////////////////////////////
  /**
   * Set het formulier op dirty
   */
  protected setDirty(): void {
    if (this.form) {
      this.log.debug(`setDirty`);
      this.form.form.markAsDirty();
    }
  }

  ////////////////////////////// HOOKS VOOR SUBCLASSES ////////////////////////////////////

  /**
   * Getter voor defaultSubmodels bij het ophalen van het record. Zo kan het dynamischer dan via defaultSubmodels
   * @protected
   */
  protected get subModels(): (typeof Basemodel)[] {
    return this.defaultSubmodels;
  }

  /**
   * Getter voor defaultSuboptions bij het ophalen van het record. Zo kan het dynamische dan via defaultSuboptions direct.
   * @protected
   */
  protected get subOptions(): [typeof Basemodel, hash<any>][] {
    return this.defaultSuboptions;
  }

  /**
   * Pas het opgehaalde record eventueel nog even aan
   */
  protected async afterHaaldata(): Promise<void> {
    //standaard: doe niets
  }

  /**
   * Als de lookups opnieuw zijn opgehaald. Bij haaldata worden lookups ook gehaald: dan runt deze hook voor afterHaaldata
   * @protected
   */
  protected afterHaalLookups(): void {

  }

  /**
   * Voor het saven aanpassen van de data zoals het opgeslagen wordt (bijvoorbeeld een vreemde _connected erin). Het teruggegeven resultaat wordt als data meegegeven. Standaard gewoon de savedata van het model
   */
  protected savedata(): Datarecord {
    return this.record.savedata();
  }

  /**
   * Na succesvol opslaan. Wordt door de standaard savehandler aangeroepen. Standaard sluit dit de dialoog met het bewerkte record als resultaat
   */
  protected async afterSave(): Promise<void> {
    void this.closeWindow(this.record, true); //geen dialoog meer
  }

  /**
   * Na succesvol verwijderen. Standaard sluit dit de dialoog
   */

  protected afterDelete(): void {
    void this.closeWindow();
  }

  /**
   * Hook na het instellen van het record en de lookups (of de conclusie dat dit niet hoeft)
   */
  protected async dataGezet(): Promise<void> {

  }
}
